<template>
    <MainContent title="Inventario de indumentarias">
        <TabsPanel
            :tabs="inventoryTabs"
            :initial-tab="'vendidas'"
            @tab-change="onTabChange"
        >
            <template #tab-vendidas>
                <template v-if="loading">
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-5 tw:p-4">
                        <Skeleton v-for="n in 4" :key="n" padding="tw:p-5" variant="tw:bg-white tw:shadow-sm">
                            <template #skeleton>
                                <div class="tw:flex tw:flex-col tw:gap-4">
                                    <div class="tw:flex tw:items-center tw:gap-3">
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-10 tw:rounded-xl tw:shrink-0" />
                                        <div class="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:gap-1">
                                            <SkeletonItem height="tw:h-4" customClass="tw:w-3/4 tw:rounded tw:mb-1" />
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-1/4 tw:rounded" />
                                        </div>
                                    </div>
                                    <div class="tw:flex tw:flex-wrap tw:gap-2">
                                        <div v-for="n in 5" :key="'pill-'+n" class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-xl tw:px-3 tw:py-2 tw:min-w-[52px]">
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-5 tw:rounded" />
                                            <SkeletonItem height="tw:h-5" customClass="tw:w-4 tw:rounded" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Skeleton>
                    </div>
                </template>
                <template v-else-if="!tabsData.vendidas.items.length">
                    <Card customClasses="tw:p-12 tw:text-center">
                        <div class="tw:w-14 tw:h-14 tw:mx-auto tw:mb-4 tw:rounded-full tw:bg-blue-50 tw:flex tw:items-center tw:justify-center">
                            <i class="fas fa-tshirt tw:text-xl tw:text-blue-500"></i>
                        </div>
                        <p class="tw:text-gray-400 tw:text-sm">No hay datos de ventas</p>
                    </Card>
                </template>
                <template v-else>
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-5 tw:p-4">
                        <Card v-for="item in groupedByPrenda(tabsData.vendidas.items)" :key="item.prenda" customClasses="tw:p-5 hover:tw:shadow-md tw:transition-shadow">
                            <div class="tw:flex tw:flex-col tw:gap-4">
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <div class="tw:w-10 tw:h-10 tw:rounded-xl tw:bg-gray-100 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:overflow-hidden">
                                        <img v-if="getImageUrl(item.prenda)" :src="getImageUrl(item.prenda)" class="tw:w-full tw:h-full tw:object-cover" />
                                        <i v-else class="fas fa-tshirt tw:text-sm tw:text-gray-400"></i>
                                    </div>
                                    <div class="tw:flex-1 tw:min-w-0">
                                        <h3 class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:truncate">{{ item.prenda }}</h3>
                                        <p class="tw:text-xs tw:text-gray-500 tw:font-medium tw:m-0">{{ item.total }} vendidas</p>
                                    </div>
                                </div>
                                <div class="tw:flex tw:flex-wrap tw:gap-2">
                                    <div
                                        v-for="talla in item.tallas"
                                        :key="talla.talla"
                                        class="tw:flex tw:flex-col tw:items-center tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-xl tw:px-3 tw:py-2 tw:min-w-[52px]"
                                    >
                                        <span class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase">{{ talla.talla }}</span>
                                        <span class="tw:text-lg tw:font-bold tw:text-gray-700 tw:tabular-nums">{{ talla.cantidad }}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </template>
            </template>

            <template #tab-stock>
                <template v-if="loading">
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-5 tw:p-4">
                        <Skeleton v-for="n in 4" :key="n" padding="tw:p-5" variant="tw:bg-white tw:shadow-sm">
                            <template #skeleton>
                                <div class="tw:flex tw:flex-col tw:gap-4">
                                    <div class="tw:flex tw:items-center tw:gap-3">
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-10 tw:rounded-xl tw:shrink-0" />
                                        <div class="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:gap-1">
                                            <SkeletonItem height="tw:h-4" customClass="tw:w-3/4 tw:rounded tw:mb-1" />
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-1/4 tw:rounded" />
                                        </div>
                                    </div>
                                    <div class="tw:flex tw:flex-wrap tw:gap-2">
                                        <div v-for="n in 5" :key="'pill-'+n" class="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-xl tw:px-3 tw:py-2 tw:min-w-[52px]">
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-5 tw:rounded" />
                                            <SkeletonItem height="tw:h-5" customClass="tw:w-4 tw:rounded" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Skeleton>
                    </div>
                </template>
                <template v-else-if="!tabsData.stock.items.length">
                    <Card customClasses="tw:p-12 tw:text-center">
                        <div class="tw:w-14 tw:h-14 tw:mx-auto tw:mb-4 tw:rounded-full tw:bg-blue-50 tw:flex tw:items-center tw:justify-center">
                            <i class="fas fa-tshirt tw:text-xl tw:text-blue-500"></i>
                        </div>
                        <p class="tw:text-gray-400 tw:text-sm">No hay datos de stock</p>
                    </Card>
                </template>
                <template v-else>
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-5 tw:p-4">
                        <Card v-for="item in groupedByPrenda(tabsData.stock.items)" :key="item.prenda" customClasses="tw:p-5 hover:tw:shadow-md tw:transition-shadow">
                            <div class="tw:flex tw:flex-col tw:gap-4">
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <div class="tw:w-10 tw:h-10 tw:rounded-xl tw:bg-gray-100 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:overflow-hidden">
                                        <img v-if="getImageUrl(item.prenda)" :src="getImageUrl(item.prenda)" class="tw:w-full tw:h-full tw:object-cover" />
                                        <i v-else class="fas fa-tshirt tw:text-sm tw:text-gray-400"></i>
                                    </div>
                                    <div class="tw:flex-1 tw:min-w-0">
                                        <h3 class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:truncate">{{ item.prenda }}</h3>
                                        <p class="tw:text-xs tw:text-gray-500 tw:font-medium tw:m-0">{{ item.total }} en stock</p>
                                    </div>
                                </div>
                                <div class="tw:flex tw:flex-wrap tw:gap-2">
                                    <div
                                        v-for="talla in item.tallas"
                                        :key="talla.talla"
                                        class="tw:flex tw:flex-col tw:items-center tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-xl tw:px-3 tw:py-2 tw:min-w-[52px]"
                                    >
                                        <span class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase">{{ talla.talla }}</span>
                                        <span class="tw:text-lg tw:font-bold tw:text-gray-700 tw:tabular-nums">{{ talla.cantidad }}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </template>
            </template>

            <template #tab-recogidas>
                <template v-if="loading">
                    <div class="tw:flex tw:flex-col tw:gap-5 tw:p-4">
                        <Skeleton v-for="n in 3" :key="n" padding="tw:p-5" variant="tw:bg-white tw:shadow-sm">
                            <template #skeleton>
                                <div class="tw:flex tw:flex-col tw:gap-4">
                                    <!-- Header: avatar + title + subtitle -->
                                    <div class="tw:flex tw:items-center tw:gap-3">
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-10 tw:rounded-xl tw:shrink-0" />
                                        <div class="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:gap-1">
                                            <SkeletonItem height="tw:h-4" customClass="tw:w-1/4 tw:rounded tw:mb-1" />
                                            <SkeletonItem height="tw:h-2" customClass="tw:w-20 tw:rounded" />
                                        </div>
                                    </div>
                                    <!-- Progress bar + percentage -->
                                    <div class="tw:flex tw:items-center tw:gap-3">
                                        <SkeletonItem height="tw:h-2.5" customClass="tw:flex-1 tw:rounded-full" />
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-10 tw:rounded" />
                                    </div>
                                    <!-- Talla grid (5 pills) -->
                                    <div class="tw:grid tw:grid-cols-2 tw:sm:grid-cols-3 tw:md:grid-cols-4 tw:gap-2">
                                        <div v-for="n in 5" :key="'rt-'+n" class="tw:flex tw:flex-col tw:items-center tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-xl tw:px-3 tw:py-2.5">
                                            <div class="tw:mb-1.5">
                                                <SkeletonItem height="tw:h-3" customClass="tw:w-5 tw:rounded" />
                                            </div>
                                            <div class="tw:flex tw:items-baseline tw:gap-0.5 tw:mb-1.5">
                                                <SkeletonItem height="tw:h-4" customClass="tw:w-4 tw:rounded" />
                                                <SkeletonItem height="tw:h-3" customClass="tw:w-6 tw:rounded" />
                                            </div>
                                            <SkeletonItem height="tw:h-1.5" customClass="tw:w-full tw:rounded-full" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Skeleton>
                    </div>
                </template>
                <template v-else-if="!tabsData.recogidas.items.length">
                    <Card customClasses="tw:p-12 tw:text-center">
                        <div class="tw:w-14 tw:h-14 tw:mx-auto tw:mb-4 tw:rounded-full tw:bg-blue-50 tw:flex tw:items-center tw:justify-center">
                            <i class="fas fa-tshirt tw:text-xl tw:text-purple-500"></i>
                        </div>
                        <p class="tw:text-gray-400 tw:text-sm">No hay datos de recogidas</p>
                    </Card>
                </template>
                <template v-else>
                    <div class="tw:flex tw:flex-col tw:gap-5 tw:p-4">
                        <Card v-for="item in groupedByPrenda(tabsData.recogidas.items)" :key="item.prenda" customClasses="tw:p-5 hover:tw:shadow-md tw:transition-shadow">
                            <div class="tw:flex tw:flex-col tw:gap-4">
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <div class="tw:w-10 tw:h-10 tw:rounded-xl tw:bg-gray-100 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:overflow-hidden">
                                        <img v-if="getImageUrl(item.prenda)" :src="getImageUrl(item.prenda)" class="tw:w-full tw:h-full tw:object-cover" />
                                        <i v-else class="fas fa-tshirt tw:text-sm tw:text-gray-400"></i>
                                    </div>
                                    <div class="tw:flex-1 tw:min-w-0">
                                        <h3 class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:truncate">{{ item.prenda }}</h3>
                                        <p class="tw:text-xs tw:text-gray-500 tw:font-medium tw:m-0">{{ item.recogidas }} de {{ item.total }} recogidas</p>
                                    </div>
                                </div>

                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <div class="tw:flex-1 tw:bg-gray-100 tw:rounded-full tw:h-2.5">
                                        <div
                                            class="tw:bg-gray-600 tw:h-2.5 tw:rounded-full tw:transition-all tw:duration-700"
                                            :style="{ width: item.total > 0 ? (item.recogidas / item.total * 100) + '%' : '0%' }"
                                        ></div>
                                    </div>
                                    <span class="tw:text-xs tw:font-semibold tw:text-gray-600 tw:tabular-nums tw:w-10 tw:text-right">
                                        {{ item.total > 0 ? Math.round(item.recogidas / item.total * 100) : 0 }}%
                                    </span>
                                </div>

                                <div class="tw:grid tw:grid-cols-2 tw:sm:grid-cols-3 tw:md:grid-cols-4 tw:gap-2">
                                    <div
                                        v-for="talla in item.tallas"
                                        :key="talla.talla"
                                        class="tw:flex tw:flex-col tw:items-center tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-xl tw:px-3 tw:py-2.5"
                                    >
                                        <span class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:mb-1.5">{{ talla.talla }}</span>
                                        <div class="tw:flex tw:items-baseline tw:gap-0.5 tw:mb-1.5">
                                            <span class="tw:text-base tw:font-bold tw:text-gray-700 tw:tabular-nums">{{ talla.recogidas }}</span>
                                            <span class="tw:text-xs tw:text-gray-400">/{{ talla.total }}</span>
                                        </div>
                                        <div class="tw:w-full tw:bg-gray-100 tw:rounded-full tw:h-1.5">
                                            <div
                                                class="tw:bg-gray-400 tw:h-1.5 tw:rounded-full tw:transition-all tw:duration-700"
                                                :style="{ width: talla.total > 0 ? (talla.recogidas / talla.total * 100) + '%' : '0%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </template>
            </template>
        </TabsPanel>
    </MainContent>
</template>

<script setup>
    import { computed, onMounted } from 'vue';
    import { useRoute } from '@/composables/common/router/useRouter';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';
    import { useIndumentariaInventario } from '@/composables/admin/bases/useIndumentariaInventario';

    const route = useRoute();
    const listaId = computed(() => route.params.lista_id);

    const {
        loading,
        tabsData,
        onTabChange,
        init,
        getImageUrl,
    } = useIndumentariaInventario(listaId);

    const inventoryTabs = [
        { key: 'vendidas', label: 'Vendidas' },
        { key: 'stock', label: 'Stock' },
        { key: 'recogidas', label: 'Recogidas' },
    ];

    function groupedByPrenda(items) {
        const groups = [];
        const seen = new Map();

        for (const item of items) {
            let group = seen.get(item.prenda);
            if (!group) {
                group = { prenda: item.prenda, tallas: [], total: 0, recogidas: 0 };
                seen.set(item.prenda, group);
                groups.push(group);
            }
            const cantidad = Number(item.cantidad) || 0;
            const sinRecoger = Number(item.sin_recoger) || 0;
            const totalTalla = cantidad + sinRecoger;
            group.tallas.push({
                talla: item.talla,
                cantidad,
                recogidas: cantidad,
                sin_recoger: sinRecoger,
                total: totalTalla,
            });
            group.total += totalTalla;
            group.recogidas += cantidad;
        }

        return groups;
    }

    onMounted(() => {
        init();
    });
</script>
